import { Suspense, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { Header } from "@/components/layout/Header";
import { SidebarMenuNavLink } from "@/components/layout/SidebarMenu";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { LoginPage } from "@/features/login/components/LoginPage";
import { getRoleDashboardConfig } from "../config/roleDashboardConfig";
import type { DashboardRole } from "../types";

interface DashboardLayoutProps {
  role: DashboardRole;
  fallback?: React.ReactNode;
}

export function DashboardLayout({
  role,
  fallback = <div>Loading…</div>,
}: DashboardLayoutProps) {
  const { user } = useCurrentUser();
  // Desktop open by default, mobile closed by default.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth >= 1024;
  });
  const location = useLocation();
  const config = getRoleDashboardConfig(role);

  if (!user) {
    return <LoginPage />;
  }

  if (user.rol?.nombre !== role) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="flex h-screen flex-col bg-[var(--background-muted)] text-[var(--foreground)]">
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <div className="flex flex-1 relative">
        <SidebarMenuNavLink
          items={config.navItems}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        <main className="flex-1 overflow-auto bg-[var(--background-muted)] p-3 sm:p-4 md:p-6">
          <div className="min-w-0 rounded-xl md:rounded-2xl border border-black/5 bg-[var(--card-background)] p-3 sm:p-4 md:p-6 text-[var(--foreground)] shadow-2xl backdrop-blur-lg transition-all dark:border-white/10">
            <Suspense fallback={fallback}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
